import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Layout from '../components/Layout'
import Hero, { HeroTitle } from '../components/Hero'
import Query from '../components/Research/Query'
import { researchPaperProps } from '../constants/propTypes'
import Navigation from '../components/Research/Navigation'
import BreadCrumbs from '../components/BreadCrumbs'
import Link from '../components/Link'
import { FaClock, FaFilePdf, FaLink } from 'react-icons/fa'
import moment from 'moment'
import Tag from '../components/Research/Tag'

const BreadCrumbsContainer = styled.div`
  margin-top: 3rem;
`

const Title = styled.h1`
  a {
    color: ${({ theme }) => theme.colors.text};
    text-decoration: underline;

    &:hover {
      color: ${({ theme }) => theme.colors.interactive};
    }
  }
`

const Creators = styled.div`
  font-size: 1.8rem;
  width: 100%;
  margin: 0 -0.4rem;

  a {
    color: ${({ theme }) => theme.colors.text};
    border-bottom: 0.1rem solid ${({ theme }) => theme.colors.text};
    transition: color 0.2s ease-in-out, border-color 0.2s ease-in-out;
    margin: 0.8rem 0.4rem;

    &:hover {
      color: ${({ theme }) => theme.colors.interactive};
      border-bottom-color: ${({ theme }) => theme.colors.interactive};
    }
  }
`

const Info = styled.p`
  margin: 1.6rem 0;
  font-size: 1.6rem;

  svg {
    vertical-align: text-top;
  }

  span {
    font-weight: 600;
  }
`

const Tags = styled.p``

const Body = styled.div`
  margin: 2rem 0 4rem;
  padding-top: 2rem;
  border-top: 0.1rem solid ${({ theme }) => theme.colors.pageRule};
`

const Abstract = styled.div`
  text-align: justify;
`

const FullPaper = styled.div`
  text-align: center;
  margin: 4rem 0;
  font-size: 1.6rem;

  > a {
    display: inline-flex;
    flex-direction: column;

    > div {
      font-size: 8rem;
      margin-bottom: 1rem;
    }
  }
`

const ResearchPaper = ({ pageContext }) => {
  const getMetaTitle = (content) =>
    `${pageContext.title} - IOHK ${content.title}`
  const getMetaDescription = () =>
    [
      pageContext.title,
      pageContext.libraryCatalog,
      moment(pageContext.dateAdded).format('MMMM/YYYY')
    ].join(', ')
  return (
    <Query
      render={(content) => (
        <Layout
          headData={{
            title: getMetaTitle(content),
            meta: [
              { name: 'description', content: getMetaDescription() },
              { name: 'twitter:description', content: getMetaDescription() },
              { name: 'twitter:title', content: getMetaTitle(content) },
              { name: 'og:description', content: getMetaDescription() },
              { name: 'og:title', content: getMetaTitle(content) }
            ]
          }}
        >
          <Hero>
            <HeroTitle>{content.paper}</HeroTitle>
          </Hero>
          <Navigation />
          <BreadCrumbsContainer>
            <BreadCrumbs
              crumbs={pageContext.crumbs.map((crumb) => ({
                label: crumb.label || content.crumbs[crumb.key] || '...',
                path: crumb.path,
                active: crumb.active
              }))}
            />
          </BreadCrumbsContainer>
          <Title>
            <Link
              tracking={{ label: `research_paper_title_${pageContext.slug}` }}
              href={pageContext.url}
            >
              {pageContext.title} <FaLink />
            </Link>
          </Title>
          <Creators>
            {pageContext.creators.map((creator, index) => (
              <Link
                tracking={{ label: `research_paper_creator_${creator.key}` }}
                key={creator.key}
                href={`/research/library/authors/${creator.key}/`}
              >
                {creator.name}
                {index < pageContext.creators.length - 1 && ','}
              </Link>
            ))}
          </Creators>
          <Info>
            <span>
              <FaClock /> {moment(pageContext.dateAdded).format('MMMM/YYYY')}
            </span>
            {(pageContext.libraryCatalog ||
              pageContext.conferenceName ||
              pageContext.proceedingsTitle) && (
              <Fragment>
                ,{' '}
                {pageContext.libraryCatalog ||
                  pageContext.conferenceName ||
                  pageContext.proceedingsTitle}
              </Fragment>
            )}
          </Info>
          <Tags>
            {pageContext.tags.map((tag) => (
              <Tag key={tag} value={tag} />
            ))}
          </Tags>
          <Body>
            <Abstract>
              <div
                dangerouslySetInnerHTML={{
                  __html: pageContext.abstractNote
                    .split('\n')
                    .map((p) => `<p>${p}</p>`)
                    .join('')
                }}
              />
            </Abstract>
            <FullPaper>
              <Link
                tracking={{
                  label: `research_paper_full_paper_${pageContext.slug}`
                }}
                href={pageContext.url}
              >
                <div>
                  <FaFilePdf />
                </div>
                <p>{content.view_full_paper}</p>
              </Link>
            </FullPaper>
          </Body>
        </Layout>
      )}
    />
  )
}

ResearchPaper.propTypes = {
  pageContext: PropTypes.shape({
    ...researchPaperProps,
    crumbs: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        key: PropTypes.string,
        path: PropTypes.string.isRequired,
        active: PropTypes.bool
      })
    ).isRequired
  }).isRequired
}

export default ResearchPaper
